import type { LucideIcon } from "lucide-react";

/** The brand-guide glyph sizes (§12): 16 / 20 / 22 / 24 / 32 — no in-between values. */
export type IconSize = 16 | 20 | 22 | 24 | 32;

/**
 * Outline (the brand-guide default, §12) or filled (`fill: currentColor`). Filled is the ONE deliberate
 * exception to §12's outline rule, reserved for MEDIA-TRANSPORT controls — play / pause — where a solid
 * triangle is the universal convention — plus the lit default-band star (ART-319, PO). Not for general
 * iconography.
 */
export type IconVariant = "outline" | "filled";

export interface IconProps {
  /** A Lucide icon component (e.g. `Bell`, `RefreshCw`). Full-colour platform brand marks use `<PlatformIcon>`. */
  icon: LucideIcon;
  size?: IconSize;
  /** Accessible label for an icon-only control; omit for decorative icons (they get aria-hidden). */
  label?: string;
  className?: string;
  /** Outline (default) or filled — see {@link IconVariant}. */
  variant?: IconVariant;
}

/**
 * The single icon wrapper — enforces the brand-guide icon rules (§12: Lucide outline, 1.5px stroke,
 * one of the five named sizes), a token-sized box, and the a11y contract (icon-only buttons MUST
 * carry an aria-label). Colors follow `currentColor` so the token cascade wins.
 *
 * The 1.5px stroke supersedes the older internal 2px ratification (P-13): the brand guide is
 * explicit — "1.5px (never 1px or 2px)".
 */
export function Icon({ icon: LucideGlyph, size = 20, label, className, variant = "outline" }: IconProps) {
  return (
    <LucideGlyph
      size={size}
      strokeWidth={1.5}
      // Only SET fill when filled — passing fill={undefined} would OVERRIDE lucide's built-in `fill: "none"`
      // default, leaving the SVG to fall back to its native black fill (which filled every outline icon).
      {...(variant === "filled" ? { fill: "currentColor" } : {})}
      className={className}
      aria-hidden={label ? undefined : true}
      aria-label={label}
      role={label ? "img" : undefined}
      focusable={false}
    />
  );
}
